import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';

import Footer from '../../components/Footer';
import { Link } from '../../components/Link';
import { BreadcrumbNavigation } from '../../components/BreadcrumbNavigation';

export default function AboutEmployerScorePage() {
  const intl = useIntl();
  const pageContext = usePageContext();
  const locale = (pageContext?.locale ?? 'en') as string;

  const pillars = [
    {
      id: 'pay',
      pct: 31,
      title: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.pay.title"
          defaultMessage="Pay (posted salary bands)"
          description="Methodology pillar title"
        />
      ),
      body: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.pay.body"
          defaultMessage="When we have enough listings with structured salary ranges, we score how competitive the typical band is. Listing-level salary transparency will fold into this pillar once that signal is wired end-to-end."
          description="Methodology pillar body"
        />
      ),
    },
    {
      id: 'flex',
      pct: 25,
      title: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.flex.title"
          defaultMessage="Flexibility (remote / hybrid)"
          description="Methodology pillar title"
        />
      ),
      body: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.flex.body"
          defaultMessage="Remote and hybrid options are scored from how consistently employers advertise flexible work models across their open roles."
          description="Methodology pillar body"
        />
      ),
    },
    {
      id: 'stability',
      pct: 25,
      title: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.stability.title"
          defaultMessage="Hiring stability"
          description="Methodology pillar title"
        />
      ),
      body: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.stability.body"
          defaultMessage="We look at hiring continuity over time — employers that maintain steady, non-spiky hiring patterns score higher than those with chaotic posting behavior."
          description="Methodology pillar body"
        />
      ),
    },
    {
      id: 'growth',
      pct: 19,
      title: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.growth.title"
          defaultMessage="Growth signals"
          description="Methodology pillar title"
        />
      ),
      body: (
        <FormattedMessage
          id="aboutEmployerScore.pillar.growth.body"
          defaultMessage="When listings include seniority structure and career progression cues, we interpret that as stronger internal growth paths."
          description="Methodology pillar body"
        />
      ),
    },
  ] as const;

  return (
    <div className="min-h-screen bg-jc-page">
      <div className="max-w-3xl mx-auto p-4 pt-2 sm:pt-4 pb-16">
        <BreadcrumbNavigation
          items={[
            {
              label: intl.formatMessage({
                id: 'aboutEmployerScore.breadcrumb.parent',
                defaultMessage: 'Best Employers',
                description: 'Breadcrumb parent link label',
              }),
              href: '/best-companies',
            },
            {
              label: intl.formatMessage({
                id: 'aboutEmployerScore.breadcrumb.current',
                defaultMessage: 'Employer score',
                description: 'Breadcrumb current page label',
              }),
            },
          ]}
          className="pb-3 text-jc-muted"
          linkClassName="text-jc-muted hover:text-jc-accent transition-colors duration-200"
          chevronClassName="h-4 w-4 mx-1 sm:mx-2 text-jc-contrast-subtle"
          useChevronRightIcon
        />

        <header className="mb-8">
          <h1 className="text-3xl font-bold text-jc-ink mb-3">
            <FormattedMessage
              id="aboutEmployerScore.title"
              defaultMessage="How the Employer Score works"
              description="H1 for employer score methodology page"
            />
          </h1>
          <p className="text-jc-muted leading-relaxed">
            <FormattedMessage
              id="aboutEmployerScore.intro"
              defaultMessage="The JobCrawls Employer Score is a single 0–100 number derived from patterns we can measure consistently across job listings. It is meant to highlight employers who communicate clearly, structure compensation fairly, and advertise flexible, candidate-friendly roles — not to replace your own interviews and diligence."
              description="Intro paragraph for employer score methodology page"
            />
          </p>
          <p className="text-jc-muted leading-relaxed mt-3 rounded-xl border border-jc-border bg-jc-surface/60 px-3 py-2 text-sm">
            <FormattedMessage
              id="aboutEmployerScore.compositeScopeNote"
              defaultMessage="For now, the headline score uses only pay bands, flexibility, hiring stability, and growth — because benefits depth and listing-level transparency signals are not yet connected to the scoring pipeline. Weights below are renormalized to 100% across those four pillars."
              description="Note that composite score excludes unwired dimensions"
            />
          </p>
        </header>

        <section className="mb-10" aria-labelledby="employer-score-pillars">
          <h2 id="employer-score-pillars" className="text-lg font-semibold text-jc-ink mb-4">
            <FormattedMessage
              id="aboutEmployerScore.pillars.title"
              defaultMessage="What goes into the score"
              description="Section heading for score pillars"
            />
          </h2>

          <ol className="space-y-4">
            {pillars.map((p) => (
              <li
                key={p.id}
                className="rounded-2xl border border-jc-border bg-jc-surface p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-jc-ink">{p.title}</p>
                    <p className="text-sm text-jc-muted mt-2 leading-relaxed">{p.body}</p>
                  </div>
                  <div className="shrink-0 rounded-xl bg-jc-accent-muted px-3 py-2 text-sm font-semibold text-jc-accent tabular-nums">
                    {p.pct}%
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-10 rounded-2xl border border-jc-border bg-jc-elevated/40 p-4">
          <h2 className="text-lg font-semibold text-jc-ink mb-2">
            <FormattedMessage
              id="aboutEmployerScore.limits.title"
              defaultMessage="Important limitations"
              description="Section heading for limitations disclaimer"
            />
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-sm text-jc-muted leading-relaxed">
            <li>
              <FormattedMessage
                id="aboutEmployerScore.limits.listings"
                defaultMessage="Scores reflect what employers publish in listings we index — not internal HR policies we cannot see."
                description="Limitation bullet"
              />
            </li>
            <li>
              <FormattedMessage
                id="aboutEmployerScore.limits.notEndorsement"
                defaultMessage="A higher score is not an endorsement, guarantee of quality, or legal/financial advice."
                description="Limitation bullet"
              />
            </li>
            <li>
              <FormattedMessage
                id="aboutEmployerScore.limits.changes"
                defaultMessage="Weights and signals may evolve as we improve detection; we aim to keep the methodology directionally stable."
                description="Limitation bullet"
              />
            </li>
          </ul>
        </section>

        <p className="text-sm text-jc-muted">
          <Link href="/best-companies" locale={locale} className="text-jc-accent font-medium hover:text-jc-cta-hover underline-offset-2 hover:underline">
            <FormattedMessage
              id="aboutEmployerScore.backToRankings"
              defaultMessage="Back to Best Employers"
              description="Link back to best employers hub"
            />
          </Link>
        </p>
      </div>

      <Footer />
    </div>
  );
}
